define('home/mixins/withSubviews',[
    'underscore',
    'jquery',
    'backbone-marionette',
], function (
    _,
    $,
    Marionette
) {
    'use strict';

    /**
     * Class used internally by withSubviews mixin.
     *
     * Has very little in common with Marionette Regions.
     *
     * @param {string} selector   A jQuery selector
     */
    function Region(selector) {
        this.selector = selector;

        // set when rendered, equal to parentView.$(selector)[0];
        this.el = null;

        // set in .render()
        // NEVER assign this to a different value without calling .close()
        // first unless you want to create memory leaks
        this.currentView = null;
    }

    Region.prototype = {
        setElement: function (el) {
            this.$el = el instanceof $ ? el : $(el);
            this.el = this.$el[0];
        },

        /**
         * Renders the view parameter into this region
         * @param {Backbone.View} view
         */
        render: function (view) {
            if (this.currentView)
                return;

            var attachmentPoint = this.el;
            var $attachmentPoint = this.$el;

            // If region doesn't have an attachment point, don't render.
            // This could happen, for example, if the parent view by closing
            // the region removes the region's element from the DOM, and then
            // the parent view tries to re-render this region without
            // re-rendering itself first.
            if (!attachmentPoint)
                return;

            this.currentView = view;

            // In other words, should we append the subview?
            if (!view.$el.is(attachmentPoint))
                $attachmentPoint.append(view.el);

            view.render();
        },

        /**
         * This function is irreversible if this.el === currentView.el!
         * Views that use this region must take this into account if
         * they ever want to close and then re-open this region.
         *
         * Generally speaking the right way to close a region and then
         * re-open it is:
         *     1. parent view closes region: region.close()
         *     2. parent view re-renders itself
         *     3. parent view renders region: region.render()
         */
        close: function () {
            var view = this.currentView;

            this.el = null;
            this.currentView = null;

            if (!view)
                return;

            if (view.close)
                view.close();
            else if (view.remove)
                view.remove();
        },
    };


    function detachIfActive(region) {
        var subview = region && region.currentView;
        return (
            // Region must have a current view or else it means
            // it's been closed or not yet rendered
            subview &&

            // This view must contain subview or else it means
            // the subview has somehow already been removed
            // (and should not be marked for re-attachment)
            $.contains(this.el, subview.el) &&

            // Desired side effect (detach returns the jquery set
            // i.e., a truthy value)
            subview.$el.detach()
        );
    }


    function attachIfFound(region) {
        var selector = region.selector;
        var $regionEl = this.$(selector);

        // If a region we previously detached is not to be found in the
        // newly rendered template, we need to close it, especially since
        // some of the clean up done in the close method would have been
        // done by jQuery if we hadn't detached the region first.
        if (!$regionEl.length) {
            region.close();
            return;
        }

        var $subviewEl = region.currentView.$el;

        // A subview either shares the same element as the region or is
        // appended into that region's element.
        if ($subviewEl.is(selector)) {
            $regionEl.replaceWith($subviewEl);
            region.setElement($subviewEl);
        } else {
            $regionEl.append($subviewEl);
            region.setElement($regionEl);
        }
    }


    function triggerShow() {
        Marionette.triggerMethod.call(this, 'show');
    }


    function closeRegion(region, key) {
        if (!region)
            return;

        region.close();
        this._regions[key] = null;
    }


    var SubviewsMixin = {
        initialize: function (_initialize, options) {
            this._regions = {};
            this.initializeRegions();
            _initialize.call(this, options);
        },

        initializeRegions: function () {
            _.each(this.regions, function (selector, propertyName) {
                this[propertyName] = this.addRegion(selector);
            }, this);
        },

        addRegion: function (selector) {
            var region = this._regions[selector];

            if (!region) {
                region = new Region(selector);
                this._regions[selector] = region;
            }

            return region;
        },

        activateRegion: function (region, viewFactory) {
            var view = region.currentView;
            if (view)
                return view;

            // Important to set region element before calling the viewFactory
            // because the viewFactory may reference the region element, e.g.:
            //
            //      createSubview: function () {
            //          return new View({el: this.someRegion.el})
            //      }
            //
            region.setElement(this.$(region.selector));

            view = viewFactory.call(this);
            region.render(view);

            // Propagate show event from parent view to its subview
            view.listenTo(this, 'show', triggerShow);

            // Trigger show on the view if the containing view is
            // already showing
            if (this._isShown)
                triggerShow.call(view);

            return view;
        },

        render: function (_render) {

            // Detach subviews, if any, before (re)rendering
            // because rendering, as it is typically accomplished
            // with jQuery.html(), removes data and event handlers
            // from child elements
            var toReAttach = _.filter(this._regions, detachIfActive, this);

            _render.call(this);

            // Re-attach subviews
            _.each(toReAttach, attachIfFound, this);

            return this;
        },

        close: function (_close) {
            _.each(this._regions, closeRegion, this);
            return _close.call(this);
        },
    };

    return function withSubviews() {

        // If this mixin has already been applied ...
        if (this.initializeRegions && this.addRegion)
            return;

        this.initialize = _.wrap(this.initialize, SubviewsMixin.initialize);
        this.render = _.wrap(this.render, SubviewsMixin.render);
        this.close = _.wrap(this.close, SubviewsMixin.close);

        _.extend(this, _.pick(SubviewsMixin,
            'initializeRegions',
            'addRegion',
            'activateRegion'
        ));
    };
});

